/**
 * WhatsAppDeepLinkButton.js
 * --------------------------------------------------------------------------
 * Production-ready React component for opening WhatsApp via wa.me deep links.
 * No WhatsApp Business API, no third-party services, no cost.
 *
 * The host ERP passes the active project / client info as props; the user
 * picks a template, optionally edits the auto-generated text, and clicks
 * "Open in WhatsApp". The component opens https://wa.me/<phone>?text=<msg>
 * in a new tab. On mobile this launches the WhatsApp app; on desktop it
 * opens WhatsApp Web.
 *
 * Phone normalization rules (in order):
 *   1. Strip everything that isn't a digit.
 *   2. If the resulting string is exactly 10 digits, prepend defaultCountry.
 *   3. Final value has no leading "+", no spaces, no dashes.
 * Examples:
 *   "+91-98765-43210"           → "919876543210"
 *   "(555) 123-4567"   c="1"   → "15551234567"
 *   "9876543210"      c="91"   → "919876543210"
 *
 * Integration tip (drop into any ERP page):
 *   <WhatsAppDeepLinkButton
 *     clientName={project.client_name}
 *     clientPhone={project.client_phone}
 *     projectName={project.name}
 *     updateText="Tiling work begins Monday."
 *     senderName="Juthika · Interior Story"
 *     template="statusUpdate"
 *     defaultCountry="91"
 *     onSent={(e) => api.post(`/projects/${project.id}/activity`, e)}
 *   />
 *
 * Bonus: WhatsAppBatchSender (named export) — sends to a list of contacts
 * sequentially with a delay between sends to avoid spam flags.
 * --------------------------------------------------------------------------
 */

import { useEffect, useMemo, useRef, useState } from 'react';

/* ───── helpers ───── */

function normalizePhone(raw, defaultCountry = '91') {
  const digits = String(raw || '').replace(/\D+/g, '');
  if (!digits) return '';
  // If exactly 10 digits, prepend the country code.
  if (digits.length === 10) return `${defaultCountry}${digits}`;
  return digits;
}

function buildTemplate(kind, props) {
  const {
    clientName = 'there',
    projectName = '',
    updateText = '',
    senderName = '',
    customMessage = '',
  } = props;
  const proj = projectName ? ` for *${projectName}*` : '';
  const signoff = senderName ? `\n\n— ${senderName}` : '';

  switch (kind) {
    case 'statusUpdate':
      return (
        `Hi ${clientName},\n\n` +
        `Quick update${proj}:\n${updateText || '<add your update here>'}` +
        `\n\nLet me know if you have any questions.${signoff}`
      );
    case 'invoiceReminder':
      return (
        `Hi ${clientName},\n\n` +
        `Gentle reminder for the pending payment${proj}.\n${updateText || '<amount / due date>'}` +
        `\n\nPlease let me know once processed. Thanks!${signoff}`
      );
    case 'meetingFollowUp':
      return (
        `Hi ${clientName},\n\n` +
        `Thanks for the meeting today${proj}.\n` +
        `Quick recap & next steps:\n${updateText || '- <action item 1>\n- <action item 2>'}` +
        `\n\nDo flag anything I may have missed.${signoff}`
      );
    case 'custom':
    default:
      return customMessage || '';
  }
}

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '');
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  // Fallback for older browsers / non-secure contexts
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

/* ───── styles (inline, zero-dependency) ───── */

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #E8E6E1',
    borderRadius: 14,
    padding: 16,
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    color: '#2C2C2E',
    maxWidth: 520,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  title: { fontSize: 14, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 },
  phoneBadge: (valid) => ({
    fontSize: 11,
    fontWeight: 500,
    padding: '3px 8px',
    borderRadius: 999,
    background: valid ? '#E8F5E9' : '#FDECEC',
    color: valid ? '#1B5E20' : '#B23B3B',
    border: `1px solid ${valid ? '#A5D6A7' : '#F5C2C2'}`,
  }),
  templateRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
  templateBtn: (active) => ({
    fontSize: 11,
    fontWeight: 500,
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid',
    borderColor: active ? '#25D366' : '#D4D0C8',
    background: active ? '#25D36615' : '#fff',
    color: active ? '#1B7E3B' : '#6B7280',
    cursor: 'pointer',
    transition: 'all 120ms ease',
  }),
  preview: {
    background: '#FAFAF8',
    border: '1px solid #E8E6E1',
    borderRadius: 10,
    padding: 12,
    fontSize: 13,
    whiteSpace: 'pre-wrap',
    minHeight: 80,
    lineHeight: 1.5,
    color: '#2C2C2E',
  },
  textarea: {
    width: '100%',
    minHeight: 120,
    padding: 12,
    fontSize: 13,
    fontFamily: 'inherit',
    border: '1px solid #D4D0C8',
    borderRadius: 10,
    outline: 'none',
    resize: 'vertical',
    background: '#fff',
    color: '#2C2C2E',
    lineHeight: 1.5,
    boxSizing: 'border-box',
  },
  meta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#6B7280', marginTop: 6 },
  counterWarn: { color: '#C67D5B', fontWeight: 600 },
  counterErr: { color: '#C4727F', fontWeight: 600 },
  actions: { display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' },
  primary: (disabled) => ({
    flex: 1,
    minWidth: 180,
    padding: '10px 14px',
    background: disabled ? '#B7DBC2' : '#25D366',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    transition: 'background 120ms ease',
  }),
  secondary: {
    padding: '10px 14px',
    background: '#fff',
    color: '#2C2C2E',
    border: '1px solid #D4D0C8',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  toggleLink: {
    background: 'transparent',
    border: 'none',
    color: '#3A8C8C',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
  },
  errorBox: {
    background: '#FDECEC',
    border: '1px solid #F5C2C2',
    color: '#B23B3B',
    fontSize: 12,
    padding: '8px 10px',
    borderRadius: 8,
    marginTop: 8,
  },
  hint: { fontSize: 10, color: '#6B7280', marginTop: 12, lineHeight: 1.5 },
};

const TEMPLATES = [
  { id: 'statusUpdate',   label: '💬 Status Update' },
  { id: 'invoiceReminder', label: '📋 Invoice Reminder' },
  { id: 'meetingFollowUp', label: '📅 Meeting Follow-up' },
  { id: 'custom',          label: '✏️ Custom' },
];

const MAX_LEN = 4096; // WhatsApp practical limit

/* ───── main component ───── */

export default function WhatsAppDeepLinkButton({
  clientName = '',
  clientPhone = '',
  projectName = '',
  updateText = '',
  senderName = '',
  template = 'statusUpdate',
  customMessage = '',
  defaultCountry = '91',
  onSent,
}) {
  const [activeTemplate, setActiveTemplate] = useState(template);
  const [editing, setEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  // Recompute the auto-generated message whenever inputs change
  const autoMessage = useMemo(
    () => buildTemplate(activeTemplate, { clientName, projectName, updateText, senderName, customMessage }),
    [activeTemplate, clientName, projectName, updateText, senderName, customMessage]
  );

  // Reset edited buffer when template/inputs change (only if user isn't editing)
  useEffect(() => {
    if (!editing) setEditedMessage(autoMessage);
  }, [autoMessage, editing]);

  // The message we'll actually send
  const finalMessage = editing ? editedMessage : autoMessage;
  const finalMessageTrimmed = finalMessage.trim();
  const phone = useMemo(() => normalizePhone(clientPhone, defaultCountry), [clientPhone, defaultCountry]);
  const phoneValid = phone.length >= 10;
  const messageValid = finalMessageTrimmed.length > 0;
  const messageWithinLimit = finalMessage.length <= MAX_LEN;
  const canSend = phoneValid && messageValid && messageWithinLimit;

  const errors = [];
  if (!clientPhone) errors.push('Phone number is missing.');
  else if (!phoneValid) errors.push(`Phone number must have at least 10 digits (got ${phone.length}).`);
  if (!messageValid) errors.push('Message cannot be empty.');
  if (!messageWithinLimit) errors.push(`Message exceeds WhatsApp limit (${finalMessage.length}/${MAX_LEN}).`);

  const waUrl = canSend
    ? `https://wa.me/${phone}?text=${encodeURIComponent(finalMessage)}`
    : '';

  const counterColor =
    finalMessage.length > MAX_LEN ? styles.counterErr
    : finalMessage.length > MAX_LEN - 200 ? styles.counterWarn
    : null;

  const handleSend = () => {
    if (!canSend) return;
    const payload = {
      phone,
      message: finalMessage,
      timestamp: new Date().toISOString(),
      url: waUrl,
    };
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    if (typeof onSent === 'function') {
      try { onSent(payload); } catch (e) { /* don't break the open */ }
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(finalMessage);
    if (ok) {
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  const targetLabel = isMobile() ? '📱 WhatsApp App' : '🖥️ WhatsApp Web';

  return (
    <div style={styles.card} data-testid="whatsapp-deeplink-button">
      <div style={styles.header}>
        <h4 style={styles.title}>💬 Send via WhatsApp</h4>
        <span
          style={styles.phoneBadge(phoneValid)}
          data-testid="whatsapp-phone-badge"
          title={phoneValid ? `Will open chat with +${phone}` : 'Phone number invalid'}
        >
          {phoneValid ? `+${phone}` : '⚠ Invalid phone'}
        </span>
      </div>

      {/* Template selector */}
      <div style={styles.templateRow} role="tablist" aria-label="Message templates">
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTemplate === t.id}
            onClick={() => {
              setActiveTemplate(t.id);
              setEditing(false);
            }}
            style={styles.templateBtn(activeTemplate === t.id)}
            data-testid={`whatsapp-template-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Message area: preview or editor */}
      {editing ? (
        <textarea
          value={editedMessage}
          onChange={(e) => setEditedMessage(e.target.value)}
          placeholder="Type your message..."
          style={styles.textarea}
          data-testid="whatsapp-message-editor"
        />
      ) : (
        <div style={styles.preview} data-testid="whatsapp-message-preview">
          {finalMessage || <em style={{ color: '#6B7280' }}>No message yet. Choose a template or use Custom.</em>}
        </div>
      )}

      <div style={styles.meta}>
        <button
          type="button"
          onClick={() => {
            if (!editing) setEditedMessage(autoMessage);
            setEditing(!editing);
          }}
          style={styles.toggleLink}
          data-testid="whatsapp-toggle-edit"
        >
          {editing ? '↩ Use template' : '✏️ Edit message'}
        </button>
        <span style={counterColor || {}} data-testid="whatsapp-char-counter">
          {finalMessage.length}/{MAX_LEN}
          {finalMessage.length > MAX_LEN - 200 && finalMessage.length <= MAX_LEN && ' · approaching limit'}
          {finalMessage.length > MAX_LEN && ' · over limit'}
        </span>
      </div>

      {errors.length > 0 && (
        <div style={styles.errorBox} data-testid="whatsapp-error-box">
          {errors.map((e, i) => <div key={i}>• {e}</div>)}
        </div>
      )}

      <div style={styles.actions}>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          style={styles.primary(!canSend)}
          data-testid="whatsapp-send-btn"
          title={canSend ? `Open ${targetLabel}` : 'Fix the errors above to enable'}
        >
          📤 Open in WhatsApp
        </button>
        <button
          type="button"
          onClick={handleCopy}
          style={styles.secondary}
          data-testid="whatsapp-copy-btn"
          disabled={!messageValid}
        >
          {copied ? '✅ Copied!' : '📋 Copy Text'}
        </button>
      </div>

      <div style={styles.hint}>
        Will open <strong>{targetLabel}</strong> in a new tab. The ERP host passes
        <code style={{ background: '#F0EFEC', padding: '0 4px', margin: '0 2px', borderRadius: 4 }}>clientName</code>,
        <code style={{ background: '#F0EFEC', padding: '0 4px', margin: '0 2px', borderRadius: 4 }}>clientPhone</code>,
        <code style={{ background: '#F0EFEC', padding: '0 4px', margin: '0 2px', borderRadius: 4 }}>projectName</code> &
        <code style={{ background: '#F0EFEC', padding: '0 4px', margin: '0 2px', borderRadius: 4 }}>updateText</code> as props.
        Pass <code style={{ background: '#F0EFEC', padding: '0 4px', borderRadius: 4 }}>onSent</code> to log activity.
      </div>
    </div>
  );
}

/* ───── batch sender (named export) ───── */

/**
 * <WhatsAppBatchSender
 *   contacts={[
 *     { clientName, clientPhone, projectName, updateText },
 *     ...
 *   ]}
 *   template="statusUpdate"
 *   senderName="Juthika"
 *   defaultCountry="91"
 *   delayMs={3000}
 *   onSent={(payload) => api.post('/activity', payload)}
 *   onComplete={() => console.log('done')}
 * />
 *
 * Opens each chat in a new tab sequentially with `delayMs` between sends.
 * Browsers may block consecutive popups — see the help text inside the
 * component for how to allow popups from your ERP origin.
 */
export function WhatsAppBatchSender({
  contacts = [],
  template = 'statusUpdate',
  senderName = '',
  defaultCountry = '91',
  delayMs = 3000,
  onSent,
  onComplete,
}) {
  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const stopRef = useRef(false);

  const total = contacts.length;

  const stop = () => { stopRef.current = true; setRunning(false); };

  const run = async () => {
    if (!total || running) return;
    stopRef.current = false;
    setRunning(true);
    setIdx(0);
    setDone([]);
    setSkipped([]);

    for (let i = 0; i < contacts.length; i++) {
      if (stopRef.current) break;
      const c = contacts[i];
      setIdx(i + 1);
      const phone = normalizePhone(c.clientPhone, defaultCountry);
      if (phone.length < 10) {
        setSkipped((prev) => [...prev, { ...c, reason: 'invalid phone' }]);
        continue;
      }
      const message = buildTemplate(template, { ...c, senderName });
      if (!message.trim()) {
        setSkipped((prev) => [...prev, { ...c, reason: 'empty message' }]);
        continue;
      }
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      const payload = { phone, message, timestamp: new Date().toISOString(), url, contact: c };
      if (typeof onSent === 'function') {
        try { onSent(payload); } catch (e) { /* ignore */ }
      }
      setDone((prev) => [...prev, payload]);
      if (!win) {
        // Popup blocked — stop and surface to user
        setSkipped((prev) => [...prev, { ...c, reason: 'popup blocked' }]);
        break;
      }
      if (i < contacts.length - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    setRunning(false);
    if (!stopRef.current && typeof onComplete === 'function') {
      try { onComplete(); } catch (e) { /* ignore */ }
    }
  };

  const progressPct = total ? Math.round((idx / total) * 100) : 0;

  return (
    <div style={{ ...styles.card, maxWidth: 560 }} data-testid="whatsapp-batch-sender">
      <div style={styles.header}>
        <h4 style={styles.title}>📤 Batch WhatsApp Sender</h4>
        <span style={{ fontSize: 11, color: '#6B7280' }}>{total} contact{total === 1 ? '' : 's'}</span>
      </div>

      {/* Progress bar */}
      {(running || idx > 0) && (
        <div style={{ marginBottom: 10 }} data-testid="whatsapp-batch-progress">
          <div style={{
            height: 8, background: '#F0EFEC', borderRadius: 4, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: `${progressPct}%`, background: '#25D366',
              transition: 'width 200ms ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            <span>{idx}/{total} sent</span>
            <span>{done.length} ok · {skipped.length} skipped</span>
          </div>
        </div>
      )}

      <div style={{ ...styles.actions, marginTop: 0 }}>
        <button
          type="button"
          onClick={run}
          disabled={running || total === 0}
          style={styles.primary(running || total === 0)}
          data-testid="whatsapp-batch-start"
        >
          {running ? `Sending… (${idx}/${total})` : `📤 Send to ${total} contact${total === 1 ? '' : 's'}`}
        </button>
        {running && (
          <button type="button" onClick={stop} style={styles.secondary} data-testid="whatsapp-batch-stop">
            ⏹ Stop
          </button>
        )}
      </div>

      {skipped.length > 0 && (
        <div style={styles.errorBox}>
          <strong>Skipped {skipped.length}:</strong>
          <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
            {skipped.slice(0, 5).map((s, i) => (
              <li key={i} style={{ fontSize: 11 }}>{s.clientName || s.clientPhone || '?'} — {s.reason}</li>
            ))}
            {skipped.length > 5 && <li style={{ fontSize: 11 }}>…and {skipped.length - 5} more</li>}
          </ul>
        </div>
      )}

      <div style={styles.hint}>
        Tip: Browsers throttle consecutive popups. Allow popups from this site, or
        use the single-send component if your browser blocks the batch.
        Delay between sends: <strong>{delayMs}ms</strong>.
      </div>
    </div>
  );
}
